// InvoiceStats.jsx
//
// ⚠️ مصمَّمة بالكامل بـ inline styles (بدون مكوّنات MUI Grid/Card/Table) عمداً:
// المشروع يحمّل Tailwind (@tailwind base بـsrc/index.css) و MUI بنفس الوقت،
// وبإصدارات @mui/material متضاربة مع @mui/system/@mui/utils (v6 مقابل v5) -
// هذا خلط معروف بكسر تخطيط MUI Grid وأحجام Typography بشكل غير متوقع. لتفادي
// أي تصادم/تراكب بالتصميم، هذي الصفحة تعتمد فقط عناصر HTML عادية + CSS Grid/
// Flexbox عبر style مباشر (نفس أسلوب النسخة الأصلية من هذا الملف)، فالتخطيط
// مضمون ثابت بغض النظر عن أي CSS خارجي بالمشروع.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ff9800",
  "#d32f2f",
  "#9c27b0",
  "#00838f",
  "#c2185b",
  "#5d4037",
  "#455a64",
  "#7cb342",
];

const TOP_SIZES_IN_CHART = 8;
const TOP_CUSTOMERS_COUNT = 5;

// 🔑 نفس دالة توحيد النص المستخدمة بـInvoiceListPage.jsx's normalizeText
const normalizeText = (str) =>
  String(str || "").trim().toLowerCase().replace(/\s+/g, " ");

// 🔑 نفس دالة كشف التكرار حرفياً (InvoiceListPage.jsx's getDuplicateKey) -
// ضرورية هنا: أحياناً تنولد أكثر من وثيقة Firestore لنفس الفاتورة (خلل قديم
// معروف بالنظام). بدونها كنا سنعدّ نفس الفاتورة المعتمدة مرتين إذا انولدت لها
// وثيقة مكررة - وهذا يكسر تطابق الأرقام مع تصدير الإكسل.
const getDuplicateKey = (inv) => {
  const number = normalizeText(inv.invoiceNumber);
  if (number) return `num:${number}`;
  return `fallback:${normalizeText(inv.customerName)}__${
    inv.lastFinalTotal ?? ""
  }__${inv.lastVersionNumber ?? ""}`;
};

const getSortTime = (inv) =>
  inv.lastUpdatedAt?.seconds ?? inv.createdAt?.seconds ?? 0;

const isInvoiceApproved = (inv) => !!inv.approvedVersionId;

// نفس منطق قراءة أبعاد العنصر المستخدم بالباك اند
// (invoiceController.js's getInvoiceItemDimensionsForCost) و
// InvoiceListPage.jsx's parseSize، حتى تتطابق أرقام هذي الصفحة مع باقي
// الصفحات: القياسات المخصّصة (sizeSource === "custom") تُقرأ من
// customWidth/customHeight وليس sizeKey.
const getItemDimensions = (item) => {
  if (item.sizeSource === "custom") {
    return {
      width: Number(item.customWidth || 0),
      height: Number(item.customHeight || 0),
    };
  }
  if (item.sizeKey) {
    const clean = String(item.sizeKey).replace("×", "x").replace(/\s/g, "");
    const [w, h] = clean.split("x").map(Number);
    return { width: Number(w || 0), height: Number(h || 0) };
  }
  return { width: 0, height: 0 };
};

const getSizeLabel = (item, width, height) => {
  if (item.sizeSource === "custom") return `${width}×${height} (مخصص)`;
  return String(item.sizeKey || "").replace("x", "×");
};

const formatIQD = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString("en-US")} د.ع`;

const formatMonthLabel = (month) => {
  const [year, m] = month.split("-");
  return `${m}/${year}`;
};

// ===== أنماط ثابتة موحّدة (حجم خط واحد لكل مستوى بكل الصفحة) =====
const styles = {
  page: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "28px 20px 60px",
    direction: "rtl",
    fontFamily: "inherit",
    color: "#1f2430",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 22,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 800,
    margin: 0,
    lineHeight: 1.3,
  },
  backButton: {
    background: "#fff",
    border: "1px solid #d7dbe3",
    color: "#333",
    padding: "9px 18px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: 16,
    background: "#f4f6f9",
    padding: "16px 18px",
    borderRadius: 14,
    marginBottom: 24,
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#5b6470",
  },
  dateInput: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d7dbe3",
    fontSize: 14,
    fontFamily: "inherit",
    color: "#1f2430",
    background: "#fff",
  },
  resetButton: {
    background: "transparent",
    border: "none",
    color: "#1976d2",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    padding: "8px 4px",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 1px 6px rgba(20,20,43,0.08)",
    height: "100%",
    boxSizing: "border-box",
  },
  cardLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "#6b7280",
    margin: "0 0 8px",
  },
  cardValue: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    color: "#151a24",
  },
  cardSub: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 6,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 800,
    margin: "0 0 14px",
    color: "#1f2430",
  },
  gridAuto: (min) => ({
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    gap: 16,
  }),
  emptyState: {
    background: "#fff",
    borderRadius: 16,
    padding: 40,
    textAlign: "center",
    color: "#6b7280",
    fontSize: 15,
    boxShadow: "0 1px 6px rgba(20,20,43,0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13.5,
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f4f6f9",
    padding: "10px 8px",
    textAlign: "center",
    fontWeight: 700,
    color: "#4b5563",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 8px",
    textAlign: "center",
    borderBottom: "1px solid #eef0f3",
    color: "#1f2430",
  },
  tableScroll: {
    maxHeight: 420,
    overflowY: "auto",
  },
};

export default function InvoiceStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const invoicesSnap = await getDocs(collection(db, "invoices"));
        const allInvoices = invoicesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // الأحدث أولاً - حتى نختار الوثيقة الصحيحة عند وجود تكرار (نفس ترتيب
        // InvoiceListPage.jsx's loadInvoices)
        allInvoices.sort((a, b) => getSortTime(b) - getSortTime(a));

        // 🧹 نجمع الوثائق حسب مفتاح التكرار (رقم الفاتورة أساساً)، ومن كل
        // مجموعة نختار أحدث وثيقة "معتمدة" فعلاً - نفس منطق التصدير لإكسل
        // بالضبط (InvoiceListPage.jsx's exportToExcel)، لضمان عدم احتساب نفس
        // الفاتورة مرتين ولضمان مطابقة الأرقام هنا لأرقام الإكسل تماماً.
        const groups = new Map();
        allInvoices.forEach((inv) => {
          const key = getDuplicateKey(inv);
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(inv);
        });

        const approvedUniqueInvoices = [];
        groups.forEach((candidates) => {
          const approved = candidates.find(isInvoiceApproved);
          if (approved) approvedUniqueInvoices.push(approved);
        });

        // ✅ نعتمد فقط الفواتير المعتمدة كـ"مبيعات فعلية" - نفس المرجعية
        // المستخدمة بباقي النظام (اعتماد الفاتورة هو لحظة تحويلها لبيع حقيقي:
        // يُنشئ الطلب وقيود المحاسبة، راجع invoiceController.js's
        // approveInvoice). الفاتورة غير المعتمدة تبقى مسودة/عرض سعر ولا
        // تُحتسب "مباعة".
        const results = await Promise.all(
          approvedUniqueInvoices.map(async (invoice) => {
            const versionRef = doc(
              db,
              "invoices",
              invoice.id,
              "versions",
              invoice.approvedVersionId
            );
            const versionSnap = await getDoc(versionRef);
            if (!versionSnap.exists()) return null;

            const data = versionSnap.data().data || {};
            return {
              id: invoice.id,
              invoiceNumber: data.invoiceNumber || invoice.invoiceNumber || "",
              customerName: data.customerName || invoice.customerName || "",
              date: data.date || "",
              finalTotal: Number(
                invoice.approvedFinalTotal ?? data.finalTotal ?? 0
              ),
              items: Array.isArray(data.items) ? data.items : [],
            };
          })
        );

        setApprovedInvoices(results.filter(Boolean));
      } catch (err) {
        console.error("خطأ أثناء جلب إحصائيات الفواتير:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredInvoices = useMemo(() => {
    return approvedInvoices.filter((inv) => {
      if (fromDate && (!inv.date || inv.date < fromDate)) return false;
      if (toDate && (!inv.date || inv.date > toDate)) return false;
      return true;
    });
  }, [approvedInvoices, fromDate, toDate]);

  const stats = useMemo(() => {
    let totalPanels = 0;
    let standardQty = 0;
    let customQty = 0;
    const sizes = {};
    const monthly = {};
    const customers = {};

    filteredInvoices.forEach((inv) => {
      let invoiceHasPanels = false;

      inv.items.forEach((item) => {
        const { width, height } = getItemDimensions(item);
        const quantity = Number(item.quantity || 0);
        // نفس شرط تخطي العناصر غير الصالحة المستخدم بباقي النظام
        // (calculateInvoiceProductionCosts / InvoiceListPage.jsx export)
        if (width <= 0 || height <= 0 || quantity <= 0) return;

        invoiceHasPanels = true;
        const isCustom = item.sizeSource === "custom";
        const label = getSizeLabel(item, width, height);
        const key = `${width}x${height}${isCustom ? "-custom" : ""}`;
        const lineTotal = Number(
          item.total || Number(item.pricePerPanel || 0) * quantity
        );

        totalPanels += quantity;
        if (isCustom) customQty += quantity;
        else standardQty += quantity;

        if (!sizes[key]) sizes[key] = { label, qty: 0, revenue: 0 };
        sizes[key].qty += quantity;
        sizes[key].revenue += lineTotal;

        if (inv.date) {
          const month = inv.date.slice(0, 7);
          if (!monthly[month]) monthly[month] = { qty: 0, revenue: 0 };
          monthly[month].qty += quantity;
          monthly[month].revenue += lineTotal;
        }
      });

      if (invoiceHasPanels) {
        const name = inv.customerName || "بدون اسم";
        if (!customers[name])
          customers[name] = { revenue: 0, invoiceCount: 0 };
        customers[name].revenue += inv.finalTotal || 0;
        customers[name].invoiceCount += 1;
      }
    });

    const sizeRows = Object.values(sizes).sort((a, b) => b.qty - a.qty);
    const topSize = sizeRows[0] || null;

    // 📊 لرسم بياني مقروء: أعلى N قياساً + تجميع الباقي بفئة "أخرى" - عرض كل
    // القياسات (قد تتجاوز العشرات مع المقاسات المخصّصة) بمخطط واحد يخلق فوضى
    // غير مقروءة. التفاصيل الكاملة تبقى بالجدول تحتها.
    const topSizesForChart = sizeRows.slice(0, TOP_SIZES_IN_CHART).map((s) => ({
      label: s.label,
      qty: s.qty,
    }));
    const restQty = sizeRows
      .slice(TOP_SIZES_IN_CHART)
      .reduce((sum, s) => sum + s.qty, 0);
    if (restQty > 0) {
      topSizesForChart.push({ label: "أخرى", qty: restQty });
    }

    const monthlyRows = Object.entries(monthly)
      .map(([month, v]) => ({ month, ...v }))
      .sort((a, b) => (a.month > b.month ? 1 : -1));

    const topCustomers = Object.entries(customers)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, TOP_CUSTOMERS_COUNT);

    const totalRevenue = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.finalTotal || 0),
      0
    );

    return {
      totalPanels,
      totalRevenue,
      approvedInvoicesCount: filteredInvoices.length,
      avgInvoiceValue: filteredInvoices.length
        ? totalRevenue / filteredInvoices.length
        : 0,
      sizeRows,
      topSize,
      topSizesForChart,
      standardVsCustom: [
        { name: "مقاسات جاهزة", value: standardQty },
        { name: "مقاسات مخصصة", value: customQty },
      ].filter((s) => s.value > 0),
      monthlyRows,
      topCustomers,
    };
  }, [filteredInvoices]);

  if (loading) {
    return (
      <div style={{ ...styles.page, textAlign: "center" }}>
        <p style={{ fontSize: 15 }}>⏳ جارِ التحميل...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.pageTitle}>📊 إحصائيات الفواتير</h1>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          ⬅️ رجوع
        </button>
      </div>

      {/* فلتر الفترة الزمنية */}
      <div style={styles.filterBar}>
        <div style={styles.filterField}>
          <span style={styles.filterLabel}>من تاريخ</span>
          <input
            type="date"
            style={styles.dateInput}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div style={styles.filterField}>
          <span style={styles.filterLabel}>إلى تاريخ</span>
          <input
            type="date"
            style={styles.dateInput}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        {(fromDate || toDate) && (
          <button
            style={styles.resetButton}
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
          >
            إعادة تعيين الفترة
          </button>
        )}
      </div>

      {filteredInvoices.length === 0 ? (
        <div style={styles.emptyState}>
          لا توجد فواتير معتمدة ضمن الفترة المحددة.
        </div>
      ) : (
        <>
          {/* بطاقات المؤشرات الرئيسية */}
          <div style={{ ...styles.gridAuto(220), marginBottom: 20 }}>
            <div style={styles.card}>
              <p style={styles.cardLabel}>📦 إجمالي عدد اللوحات المباعة</p>
              <p style={styles.cardValue}>
                {stats.totalPanels.toLocaleString("en-US")}
              </p>
            </div>
            <div style={styles.card}>
              <p style={styles.cardLabel}>🧾 عدد الفواتير المعتمدة</p>
              <p style={styles.cardValue}>
                {stats.approvedInvoicesCount.toLocaleString("en-US")}
              </p>
            </div>
            <div style={styles.card}>
              <p style={styles.cardLabel}>💰 إجمالي قيمة الفواتير</p>
              <p style={styles.cardValue}>{formatIQD(stats.totalRevenue)}</p>
            </div>
            <div style={styles.card}>
              <p style={styles.cardLabel}>📊 متوسط قيمة الفاتورة</p>
              <p style={styles.cardValue}>
                {formatIQD(stats.avgInvoiceValue)}
              </p>
            </div>
          </div>

          {/* أعلى قياس + جاهزة مقابل مخصصة + أكثر القياسات مبيعاً */}
          <div style={{ ...styles.gridAuto(280), marginBottom: 20 }}>
            <div style={styles.card}>
              <p style={styles.cardLabel}>🏆 أعلى قياس مبيعاً</p>
              {stats.topSize ? (
                <>
                  <p style={styles.cardValue}>{stats.topSize.label}</p>
                  <p style={styles.cardSub}>
                    {stats.topSize.qty.toLocaleString("en-US")} لوحة —{" "}
                    {formatIQD(stats.topSize.revenue)}
                  </p>
                </>
              ) : (
                <p style={styles.cardSub}>لا توجد بيانات</p>
              )}

              <div style={{ marginTop: 18 }}>
                <p style={styles.cardLabel}>🎯 جاهزة مقابل مخصصة</p>
                <div style={{ width: "100%", height: 170 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.standardVsCustom}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={38}
                        outerRadius={62}
                      >
                        {stats.standardVsCustom.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? "#1976d2" : "#ff9800"}
                          />
                        ))}
                      </Pie>
                      <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        iconSize={10}
                      />
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${value.toLocaleString("en-US")} لوحة`,
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div style={{ ...styles.card, gridColumn: "span 2" }}>
              <p style={styles.sectionTitle}>📐 أكثر القياسات مبيعاً</p>
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.topSizesForChart}
                    layout="vertical"
                    margin={{ top: 4, right: 20, bottom: 4, left: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={130}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                      formatter={(value) => [
                        `${value.toLocaleString("en-US")} لوحة`,
                        "الكمية",
                      ]}
                    />
                    <Bar dataKey="qty">
                      {stats.topSizesForChart.map((_, index) => (
                        <Cell
                          key={`bar-cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* المبيعات الشهرية */}
          {stats.monthlyRows.length > 1 && (
            <div style={{ ...styles.card, marginBottom: 20 }}>
              <p style={styles.sectionTitle}>📈 عدد اللوحات المباعة شهرياً</p>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.monthlyRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonthLabel}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      labelFormatter={formatMonthLabel}
                      formatter={(value) => [
                        `${value.toLocaleString("en-US")} لوحة`,
                        "الكمية",
                      ]}
                    />
                    <Bar dataKey="qty" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* جدول القياسات الكامل + أفضل الزبائن */}
          <div style={styles.gridAuto(340)}>
            <div style={styles.card}>
              <p style={styles.sectionTitle}>
                📋 كل القياسات ({stats.sizeRows.length})
              </p>
              <div style={styles.tableScroll}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>القياس</th>
                      <th style={styles.th}>عدد اللوحات</th>
                      <th style={styles.th}>النسبة</th>
                      <th style={styles.th}>إجمالي السعر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.sizeRows.map((s) => (
                      <tr key={s.label}>
                        <td style={styles.td}>{s.label}</td>
                        <td style={styles.td}>
                          {s.qty.toLocaleString("en-US")}
                        </td>
                        <td style={styles.td}>
                          {stats.totalPanels
                            ? `${((s.qty / stats.totalPanels) * 100).toFixed(
                                1
                              )}%`
                            : "-"}
                        </td>
                        <td style={styles.td}>{formatIQD(s.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.card}>
              <p style={styles.sectionTitle}>👥 أفضل الزبائن</p>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>الزبون</th>
                    <th style={styles.th}>عدد الفواتير</th>
                    <th style={styles.th}>إجمالي القيمة</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topCustomers.map((c) => (
                    <tr key={c.name}>
                      <td style={styles.td}>{c.name}</td>
                      <td style={styles.td}>
                        {c.invoiceCount.toLocaleString("en-US")}
                      </td>
                      <td style={styles.td}>{formatIQD(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
